import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@design-system/Button/Button";
import {
    PlainModal,
    StandardModalFooter,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import styles from "./CreatedTokenModal.module.scss";

interface CreatedTokenModalProps {
    plaintext: string;
    onDismiss: () => void;
    title?: string;
    plaintextTestId?: string;
}

export const CreatedTokenModal: React.FC<CreatedTokenModalProps> = ({
    plaintext,
    onDismiss,
    title = "Token created, please copy it now",
    plaintextTestId = "personal-access-token-plaintext",
}) => {
    const [isCopied, setIsCopied] = useState(false);
    const resetCopyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );

    const clearResetCopyTimeout = useCallback(() => {
        if (resetCopyTimeoutRef.current) {
            clearTimeout(resetCopyTimeoutRef.current);
            resetCopyTimeoutRef.current = null;
        }
    }, []);

    useEffect(() => clearResetCopyTimeout, [clearResetCopyTimeout]);

    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(plaintext);
            clearResetCopyTimeout();
            setIsCopied(true);
            resetCopyTimeoutRef.current = setTimeout(() => {
                setIsCopied(false);
                resetCopyTimeoutRef.current = null;
            }, 2000);
        } catch {
            clearResetCopyTimeout();
            setIsCopied(false);
        }
    }, [clearResetCopyTimeout, plaintext]);

    return (
        <PlainModal show onHide={onDismiss} size="md">
            <StandardModalHeader>{title}</StandardModalHeader>

            <div className="mb-4">
                <p>Your token won&apos;t be shown again.</p>
                <code
                    data-testid={plaintextTestId}
                    className={styles.inlineCode}
                >
                    {plaintext}
                </code>
            </div>
            <StandardModalFooter>
                <Button variant="secondary" size="md" onClick={handleCopy}>
                    {isCopied ? "Copied" : "Copy"}
                </Button>
                <Button variant="primary" size="md" onClick={onDismiss}>
                    Close
                </Button>
            </StandardModalFooter>
        </PlainModal>
    );
};
